import { ForwardedRef, forwardRef, useCallback } from 'react'
import { useSelectState } from '@react-stately/select'
import { HiddenSelect, useSelect } from '@react-aria/select'
import { SelectProps } from './Types'
import { Button } from 'blaze-ui/Button'
import { Popover } from 'components/Popover/Popover'
import Styles from './Select.module.scss'
import { ListBoxBase } from 'components/ListBox/ListBoxBase'
import { AriaListBoxOptions } from '@react-aria/listbox'
import { autoUpdate, offset, inline, shift, flip, size as sizeMiddleware } from '@floating-ui/dom'
import { useFloating } from '@floating-ui/react-dom'
import { useObjectRef } from '@react-aria/utils'
import { FloatingPortal } from '@floating-ui/react-dom-interactions'

function Select<T extends object>(props: SelectProps<T>, fRef: ForwardedRef<HTMLButtonElement>) {
  const ref = useObjectRef(fRef)
  const state = useSelectState(props)

  const {
    useButtonWidth = false,
    variant = 'tertiary',
    size = 'md',
    placement = 'bottom-start',
    placeholder,
    useChildClass = false,
    useFullWidth = false,
    isDisabled,
    ignoreHover = false,
    icon: Icon,
    endIcon = null,
    popoverClass = '',
    backdropClass = '',
    renderSelectValue = () => (state.selectedItem ? state.selectedItem.rendered : placeholder),
    spanClass = '',
    iconClass = '',
    rounded = false,
    root,
    ButtonComponent = Button,
  } = props

  const { triggerProps, valueProps, menuProps } = useSelect(props, state, ref)
  const {
    x,
    y,
    reference: triggerRef,
    floating,
  } = useFloating({
    placement: placement,
    strategy: 'fixed',
    middleware: [
      offset(4),
      inline(),
      flip(),
      shift({ padding: 4 }),
      sizeMiddleware({
        boundary: 'clippingAncestors',
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            visibility: 'visible',
          })
        },
      }),
    ],
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: true,
      })
    },
  })

  const buttonRef = useCallback((r: any) => {
    if (r) {
      triggerRef(r)
      ref.current = r
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const chevronDirection = state.isOpen ? 'up' : 'down'
  return (
    <>
      <HiddenSelect state={state} triggerRef={ref} label={props.label} name={props.name} />
      <ButtonComponent
        size={size}
        fullWidth={useFullWidth}
        variant={variant}
        {...triggerProps}
        ref={buttonRef}
        ignoreHover={ignoreHover}
        useChildClass={useChildClass}
        endIcon={endIcon || undefined}
        iconClassName={iconClass}
        isDisabled={isDisabled}
        withChevron={endIcon ? undefined : chevronDirection}
        forceActive={state.isOpen}
        rounded={rounded}
        aria-labelledby={props['aria-labelledby'] || triggerProps['aria-labelledby']}
      >
        {Icon && <Icon className={Styles.icon} />}
        <span className={spanClass} {...valueProps} id={props['aria-labelledby'] || valueProps['id']}>
          {renderSelectValue(state.selectedItem)}
        </span>
      </ButtonComponent>
      {state.isOpen && (
        <FloatingPortal id="floating-ui-root" root={root}>
          <Popover
            style={{
              position: 'fixed',
              top: y || 0,
              left: x || 0,
              visibility: 'hidden',
              width: useButtonWidth && ref.current ? ref.current.offsetWidth : 'auto',
            }}
            shouldCloseOnBlur
            isDismissable
            onClose={state.close}
            isOpen={state.isOpen}
            ref={floating}
            className={popoverClass}
            backdropClass={backdropClass}
          >
            <ListBoxBase
              {...(menuProps as AriaListBoxOptions<T>)}
              useFullWidth={useFullWidth}
              state={state}
              isVirtualized={false}
            />
          </Popover>
        </FloatingPortal>
      )}
    </>
  )
}
const _Select = forwardRef(Select)
export { _Select as Select }
